import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  Box,
  Tab,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { AppDispatch, AppState } from '../../store';
import { useTranslation, Trans } from 'react-i18next';
import {
  ECommonDialogType,
  ETabValue,
  EDataMaintenanceType,
  EDataTableType,
  foreignKeysForTable,
  EActionType,
  EModuleType,
  ECellType,
  ECellSubType,
} from '../../data/Constants';
import { UserManipulationForm } from '../Administration/Users/UserManipulationForm';
import { PriceListMapManipulation } from '../DataMaintenance/PriceListMap/PriceListMapManipulation';
import { DetailedView } from './DetailedView';
import { useAuth } from 'oidc-react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import WarningIcon from '@mui/icons-material/Warning';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FeatureFlagsManipulationForm } from '../FeatureFlags/FeatureFlagsManipulationForm';
import { ProductHierarchyCompare } from '../DataMaintenance/ProductHierarchy/ProductHierarchyCompare';
import DraggableComponent from '../DraggableComponent';
import { DetailsDialog } from './DetailsDialog';
import CancelIcon from '@mui/icons-material/Cancel';
import { PropertyManipulationForm } from '../Product/Property/PropertyManipulationForm';
import { FeaturePropertyAssociation } from '../Product/Property/FeaturePropertyAssociation';
import { resetDialogMessage } from '../../store/states/DialogSlice';
import { MultiNumericManipulationForm } from '../../components/Features/MultiNumericFeature/MultiNumericManipulationForm';
import { RoleManipulationForm } from '../../components/Administration/Roles/RoleMaipulationForm';
import { ApplicationManipulationForm } from '../../components/Administration/Applications/ApplicationManipulationForm';
import { MarketAssociationManipulationForm } from '../../components/Product/Market/MarketAssociationManipulationForm';
import { MarketManipulationForm } from '../../components/Product/Market/MarketManipulationForm';
import { LanguageManipulationForm } from '../../components/Product/Language/LanguageManipulationForm';
import { LanguageAssociationManipulationForm } from '../../components/Product/Language/LanguageAssociationMaipulationForm';
import { ApiKeyManipulationForm } from '../../components/Administration/ApiKeys/ApiKeyManipulationForm';
import { RetriggerProductReleaseForm } from '../Notification/Ace/RetriggerProductReleaseForm';
import {
  IDialogData,
  IDialogMessage,
  IMarketsPropsData,
  ILanguagePropsData,
} from 'types/IComponentTypes';
import { TFunction } from 'i18next';

interface SalesTextMessage {
  longSalesText?: string;
  shortSalesText: string;
}

interface SalesTextContentProps {
  message: SalesTextMessage;
  handleChange: (event: React.SyntheticEvent, value: string) => void;
  tabValue: string | null;
  t: TFunction;
}

interface IApiKeyCreateSuccessProps {
  apiKey: string;
  disableButton: boolean;
  enableButton: (event: React.ChangeEvent<HTMLInputElement>) => void;
  t: TFunction;
}

interface DialogData extends IDialogData {
  title?: string;

  callDeleteAPI?: () => void;
}

const LongSalesTextContent = (longSalesTextProps: SalesTextContentProps) => {
  const { message, handleChange, tabValue, t } = longSalesTextProps;
  let longSaleText = null;
  try {
    longSaleText = message.longSalesText
      ? JSON.parse(message.longSalesText)
      : message.longSalesText;
  } catch {
    longSaleText = message.longSalesText;
  }
  return (
    <TabContext value={tabValue || ETabValue.ShortSalesText}>
      <Box className="dialog-box">
        <TabList onChange={handleChange}>
          <Tab
            className="dialog-tab text-capitalize"
            value={ETabValue.ShortSalesText}
            label={t('labels.shortSalesText')}
          />
          <Tab
            className="dialog-tab text-capitalize"
            value={ETabValue.LongSalesText}
            label={t('labels.longSalesText')}
          />
        </TabList>
      </Box>
      <TabPanel className="dialog-panel pt-1" value={ETabValue.ShortSalesText}>
        {message.shortSalesText}
      </TabPanel>
      <TabPanel className="dialog-panel pt-1 " value={ETabValue.LongSalesText}>
        {parse(DOMPurify.sanitize(longSaleText))}
      </TabPanel>
    </TabContext>
  );
};

const ApiKeyCreateSuccess = (apiKeyCreateSuccessProps: IApiKeyCreateSuccessProps) => {
  const { apiKey, disableButton, enableButton, t } = apiKeyCreateSuccessProps;
  const copyText = (): void => {
    navigator.clipboard.writeText(apiKey);
  };
  return (
    <>
      <div className="d-flex align-center pb-1">
        <b>{t('labels.apiKey')}: &nbsp;</b>
        <span className="apiKey">{apiKey}</span>
        <IconButton aria-label="close" onClick={() => copyText()}>
          <ContentCopyIcon />
        </IconButton>
      </div>
      <FormControlLabel
        value={disableButton}
        checked={!disableButton}
        control={<Checkbox onChange={enableButton} />}
        label={t('labels.copyApiKey')}
        labelPlacement="end"
        name="copyKey"
      />
      <br />
      <b>{t('labels.note')}: </b>
      {t('messages.copyApiKeyMessage')}
    </>
  );
};

function getHeader(
  manipulationType: string,
  t: TFunction,
  type: string,
  name: string
): React.ReactNode {
  if (manipulationType === EActionType.Delete) {
    return (
      <Grid container sx={{ alignItems: 'center' }}>
        <>
          <WarningIcon className="warningIcon" /> &nbsp;
        </>
        <span> {t('labels.deleteConfirm')} </span>
      </Grid>
    );
  } else {
    return manipulationType === EActionType.Create
      ? t('labels.create') + ' ' + t('header.' + type)
      : t('labels.edit') + ' ' + t('header.' + type) + ' (' + name + ')';
  }
}

//update the method for reduce cognitive complexities
const getDialogTitle = (dialogData: DialogData, t: TFunction): React.ReactNode => {
  switch (dialogData.type) {
    case ECommonDialogType.ApiKeyCreateSuccess:
      return (
        <Grid container sx={{ alignItems: 'center' }}>
          <>
            <CheckCircleIcon className="successIcon" /> &nbsp;
          </>
          <span> {t('dialogBox.success')}</span>
        </Grid>
      );
    case EDataMaintenanceType.PriceListMap:
      return `${t('labels.editPriceListMap')} (${(dialogData.message as IDialogMessage).country})`;
    case EDataMaintenanceType.LanguageAssociation:
      return `${t('labels.editLanguageAssociation')} (${(dialogData.message as IDialogMessage).productCode})`;
    case EDataMaintenanceType.MarketAssociation:
      return `${t('labels.editMarketAssociation')} (${(dialogData.message as IDialogMessage).productCode})`;
    case ECommonDialogType.Image:
      return `${t('labels.image')} (${(dialogData.message as IDialogMessage).name})`;
    case ECommonDialogType.LongSalesText:
      return `${t('header.salesText')} (${(dialogData.message as IDialogMessage).materialCode})`;
    case ECommonDialogType.ShortSalesText:
      return `${t('header.salesText')} (${(dialogData.message as IDialogMessage).materialCode})`;
    case ECommonDialogType.Languages:
    case ECommonDialogType.Markets:
    case ECommonDialogType.Properties:
      return getHeader(
        dialogData.manipulationType,
        t,
        dialogData.type,
        (dialogData.message as IDialogMessage).name as string
      );
    case ECommonDialogType.PropertyAssociation:
      return `${t('header.featurePropertyAssociation')} ( ${(dialogData.message as IDialogMessage).productCode} )`;
    case ECommonDialogType.RetriggerProductRelease:
      return `${t('labels.retrigger')} (${(dialogData.message as IDialogMessage).id})`;
    case ECommonDialogType.Error:
      return (
        <>
          <CancelIcon color="error" />
          &nbsp;{t('dialogBox.error')}
        </>
      );
    default:
      if (dialogData.title) {
        return dialogData.title;
      } else {
        return getDialogTitleByManipulationType(dialogData, t);
      }
  }
};

const getDialogTitleByManipulationType = (
  dialogData: DialogData,
  t: TFunction
): React.ReactNode => {
  switch (dialogData.manipulationType) {
    case EActionType.Compare:
      return `${t('labels.compare')}`;
    case EActionType.View:
      return (
        '' +
        t('header.' + dialogData.header?.detailedViewHeader) +
        ' (' +
        (((dialogData.message as IDialogMessage)[dialogData.uniqueKey] as string | undefined) ??
          '') +
        ')'
      );
    default:
      return getDialogTitleByModuleType(dialogData, t);
  }
};

const getDialogTitleByModuleType = (dialogData: DialogData, t: TFunction): React.ReactNode => {
  switch (dialogData.moduleType) {
    case EModuleType.Administration:
    case EModuleType.Datamaintainance:
    case EModuleType.Feature:
      return getHeader(
        dialogData.manipulationType,
        t,
        dialogData.type,
        (dialogData.message as IDialogMessage).name as string
      );
    case EModuleType.FeatureFlags:
      return getHeader(
        dialogData.manipulationType,
        t,
        dialogData.type,
        (dialogData.message as IDialogMessage).flagName as string
      );

    default:
      return 'dialog';
  }
};

const getDialogActions = (
  dialogData: DialogData,
  closeDialog: () => void,
  disableButton: boolean,
  t: TFunction
): React.ReactNode => {
  if (dialogData.type === ECommonDialogType.ApiKeyCreateSuccess) {
    return (
      <Button onClick={closeDialog} disabled={disableButton}>
        {t('button.ok')}
      </Button>
    );
  } else if (
    dialogData.type === ECommonDialogType.EditLanguageAssociation ||
    dialogData.type === ECommonDialogType.EditMarketAssociation ||
    dialogData.type === ECommonDialogType.EditPriceListMap ||
    dialogData.type === EDataMaintenanceType.ProductHierarchy ||
    dialogData.type === ECommonDialogType.RetriggerProductRelease
  ) {
    return null;
  } else if (dialogData.manipulationType === EActionType.Delete) {
    return (
      <>
        <Button className="text-capitalize" onClick={closeDialog}>
          {t('button.cancel')}
        </Button>
        <Button onClick={dialogData.callDeleteAPI}>{t('button.ok')}</Button>
      </>
    );
  } else {
    return <Button onClick={closeDialog}>{t('button.ok')}</Button>;
  }
};

const CommonDialogContent = (commonDialogContentProps: {
  dialogData: DialogData;
  token: string;
  uniqueKey: string;
  t: TFunction;
  handleChange: (event: React.SyntheticEvent, value: string) => void;
  tabValue: string | null;
  disableButton: boolean;
  enableButton: (event: React.ChangeEvent<HTMLInputElement>) => void;
}): React.ReactNode => {
  const { dialogData, token, uniqueKey, t, handleChange, tabValue, disableButton, enableButton } =
    commonDialogContentProps;
  if (dialogData.manipulationType === EActionType.Delete) {
    const items = (dialogData.message as IDialogMessage).selectedIds!.join(', ');
    const message = t('messages.deleteConfirmation');
    if (dialogData.type === EDataTableType.Applications) {
      return (
        <Trans
          i18nKey={'messages.confirmation.delete.applications'}
          values={{ applicationNames: items }}
        />
      );
    } else if (dialogData.type === EDataTableType.FeatureFlags) {
      return (
        <Trans
          i18nKey={'messages.confirmation.delete.featureFlags'}
          values={{ featureFlagNames: items }}
        />
      );
    } else if (dialogData.type === EDataTableType.Prices) {
      return (
        <Trans
          i18nKey={t('messages.deletePricesConfirmation')}
          values={{ featureFlagNames: items }}
        />
      );
    } else {
      return <div className="pt-1">{message}</div>;
    }
  } else {
    switch (dialogData.type) {
      case EDataTableType.Users:
        return (
          <UserManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.Roles:
        return (
          <RoleManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.ApiKeys:
        return (
          <ApiKeyManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.Applications:
        return (
          <ApplicationManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.PriceListMap:
        return (
          <PriceListMapManipulation
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.Markets:
        return (
          <MarketManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.MarketAssociation:
        return (
          <MarketAssociationManipulationForm
            data={dialogData.message as IMarketsPropsData['data']}
            type={dialogData.manipulationType}
            id={(dialogData.message as IDialogMessage).productCode ?? ''}
          />
        );
      case EDataTableType.Languages:
        return (
          <LanguageManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case EDataTableType.LanguageAssociation:
        return (
          <LanguageAssociationManipulationForm
            data={dialogData.message as ILanguagePropsData['data']}
            type={dialogData.manipulationType}
            id={(dialogData.message as IDialogMessage).productCode ?? ''}
          />
        );
      case EDataTableType.FeatureFlags:
        return (
          <FeatureFlagsManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case ECommonDialogType.LongSalesText:
        return (
          <LongSalesTextContent
            message={dialogData.message as SalesTextMessage}
            tabValue={tabValue}
            handleChange={handleChange}
            t={t}
          />
        );
      case ECommonDialogType.Image:
        return (
          <div className="text-center">
            <img src={(dialogData.message as IDialogMessage).url} alt="message-url" />
          </div>
        );
      case ECommonDialogType.ApiKeyCreateSuccess:
        return (
          <ApiKeyCreateSuccess
            apiKey={dialogData.message as string}
            enableButton={enableButton}
            disableButton={disableButton}
            t={t}
          />
        );
      case EDataMaintenanceType.ProductHierarchy:
        return <ProductHierarchyCompare token={token} />;
      case ECommonDialogType.Error:
        return <div className="pt-1">{dialogData.message as string}</div>;
      case ECommonDialogType.Ace:
        return <div className="pt-1">{(dialogData.message as IDialogMessage).data}</div>;
      case ECommonDialogType.Queue:
        return <div className="pt-1">{dialogData.message as string}</div>;
      case ECommonDialogType.Properties:
        return (
          <PropertyManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case ECommonDialogType.PropertyAssociation:
        return (
          <FeaturePropertyAssociation
            productId={(dialogData.message as IDialogMessage).productCode as string}
            token={token}
          />
        );
      case ECommonDialogType.MultiNumericFeature:
        return (
          <MultiNumericManipulationForm
            id={(dialogData.message as IDialogMessage)[uniqueKey] as string}
            type={dialogData.manipulationType}
          />
        );
      case ECommonDialogType.RetriggerProductRelease:
        return (
          <RetriggerProductReleaseForm id={(dialogData.message as IDialogMessage).id as string} />
        );
      default:
        if (dialogData.message) {
          return dialogData.message as string;
        } else {
          return <div className="pt-1">Default div</div>;
        }
    }
  }
};

const getDialogContent = (
  dialogData: DialogData,
  disableButton: boolean,
  enableButton: (event: React.ChangeEvent<HTMLInputElement>) => void,
  t: TFunction,
  handleChange: (event: React.SyntheticEvent, value: string) => void,
  tabValue: string | null,
  token: string | null
): React.ReactNode => {
  const uniqueKey = foreignKeysForTable[dialogData.type];
  if (dialogData.type === ECellType.ArrayChips) {
    return DetailedView(
      (dialogData.message as IDialogMessage)[dialogData.header.field],
      dialogData.header.field,
      t
    );
  } else if (dialogData.type === ECellType.ObjectChips) {
    switch (dialogData.header.subType) {
      case ECellSubType.Roles:
        return DetailedView(
          (dialogData.message as IDialogMessage).roles,
          dialogData.header.field,
          t
        );
      case ECellSubType.Countires:
        return DetailedView(
          (dialogData.message as IDialogMessage).countries,
          dialogData.header.field,
          t,
          true
        );
      case ECellSubType.Languages:
        return DetailedView(
          (dialogData.message as IDialogMessage).languages,
          dialogData.header.field,
          t,
          true
        );
    }
  } else if (dialogData.type === ECellType.Details) {
    switch (dialogData.header.subType) {
      case ECellSubType.AssociatedMarkets:
        return DetailsDialog(
          (dialogData.message as IDialogMessage).markets,
          dialogData.detailsViewType,
          t,
          true
        );
      case ECellSubType.AssociatedLanguages:
        return DetailsDialog(
          (dialogData.message as IDialogMessage).languages,
          dialogData.detailsViewType,
          t,
          true
        );
      case ECellSubType.Images:
        return DetailsDialog(
          (dialogData.message as IDialogMessage).materialImages,
          dialogData.detailsViewType,
          t,
          true
        );
    }
  } else {
    return CommonDialogContent({
      dialogData,
      token: token ?? '',
      uniqueKey,
      t,
      handleChange,
      tabValue,
      disableButton,
      enableButton,
    });
  }
  return <></>;
};

const largerDialog = (dialogData: DialogData) => {
  return (
    (dialogData.manipulationType === EActionType.View && dialogData.type !== ECellType.Details) ||
    ((dialogData.type === EDataTableType.ApiKeys ||
      dialogData.type === EDataTableType.FeatureFlags ||
      dialogData.type === EDataTableType.MarketAssociation ||
      dialogData.type === EDataTableType.LanguageAssociation ||
      dialogData.type === EDataTableType.PriceListMap ||
      dialogData.type === EDataMaintenanceType.ProductHierarchy ||
      dialogData.type === EDataTableType.Properties) &&
      (dialogData.manipulationType === EActionType.Compare ||
        dialogData.manipulationType === EActionType.Create ||
        dialogData.manipulationType === EActionType.Edit))
  );
};

const xLargerDialog = (dialogData: DialogData) => {
  return (
    (dialogData.manipulationType === EActionType.View && dialogData.type === ECellType.Details) ||
    dialogData.type === EDataTableType.PropertyAssociation
  );
};

export const CommonDialog = () => {
  const auth = useAuth();
  const token = auth?.userData?.access_token;
  const [disableButton, setDisableButton] = useState(true);
  const [tabValue, setTabValue] = useState<string | null>('ShortSalesText');
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const dialogData = useSelector((state: AppState) => state.dialogData);

  const handleChange = (...params: [React.SyntheticEvent, string]) => {
    setTabValue(params[1]);
  };

  useEffect(() => {
    if (
      dialogData.type === ECommonDialogType.LongSalesText ||
      dialogData.type === ECommonDialogType.ShortSalesText
    ) {
      setTabValue(dialogData.type);
    }
  }, [dialogData.type]);

  if (!dialogData.show) {
    return null;
  }

  const closeDialog = () => {
    setDisableButton(true);
    dispatch(resetDialogMessage());
  };

  const enableButton = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setDisableButton(!event.target.checked);
  };

  return (
    <Dialog
      open={dialogData.show}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={DraggableComponent}
      className={`common-dialog-style success-dialog 
    ${
      dialogData.type !== ECommonDialogType.ApiKeyCreateSuccess &&
      dialogData.manipulationType !== EActionType.Delete &&
      dialogData.manipulationType !== ECommonDialogType.Error
        ? 'manipulationDialog'
        : ''
    } 
      ${largerDialog(dialogData) ? 'largerManipulationDialog' : ''} 
      ${xLargerDialog(dialogData) ? 'xLargerDialog' : ''}`}
    >
      <DialogTitle className="header">{getDialogTitle(dialogData, t)}</DialogTitle>

      <DialogContent className="pb-0 align-center ">
        {getDialogContent(
          dialogData,
          disableButton,
          enableButton,
          t,
          handleChange,
          tabValue,
          token ?? ''
        )}
      </DialogContent>

      <DialogActions>{getDialogActions(dialogData, closeDialog, disableButton, t)}</DialogActions>
    </Dialog>
  );
};
